import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Drawer,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Kitchen as KitchenIcon,
  KeyboardDoubleArrowLeft as CollapseIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Campaign as CampaignIcon,
  LocalOffer as LocalOfferIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 80;

const MotionBox = motion(Box);

const NavigationBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const currentWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  const showExpanded = isMobile ? true : !isCollapsed;

  const handleMobileToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = async () => {
    const { error } = await signOut();
    if (!error) navigate('/signin');
  };

  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Recipes', icon: <RestaurantMenuIcon />, path: '/recipes' },
    { text: 'Ingredients', icon: <KitchenIcon />, path: '/ingredients' },
    ...(isAdmin
      ? [
          { text: 'Influencers', icon: <CampaignIcon />, path: '/admin/influencers' },
          { text: 'Promo codes', icon: <LocalOfferIcon />, path: '/admin/promo-codes' },
          { text: 'Feedback', icon: <ForumIcon />, path: '/admin/feedback' },
        ]
      : []),
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const isActive = (path: string) =>
    path === '/dashboard' ? location.pathname === path : location.pathname.startsWith(path);

  const getUserInitials = () => {
    if (user?.email) return user.email.substring(0, 2).toUpperCase();
    return 'AD';
  };

  const navigationContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-alt)',
        position: 'relative',
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 72,
        }}
      >
        <AnimatePresence mode="wait">
          {showExpanded ? (
            <motion.div
              key="expanded-logo"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <img
                src="/images/EASYTOCOOK_long.png"
                alt="EasyToCook"
                style={{ height: 60, width: 'auto', display: 'block' }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="collapsed-logo"
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                style={{ height: 38, width: 'auto', display: 'block' }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Navigation Items */}
      <List
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: showExpanded ? 1.5 : 1,
          py: 2,
        }}
      >
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!showExpanded ? item.text : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: '14px',
                    py: 1.75,
                    px: showExpanded ? 2 : 1.5,
                    justifyContent: showExpanded ? 'flex-start' : 'center',
                    position: 'relative',
                    backgroundColor: active ? 'var(--accent-soft)' : 'transparent',
                    '&::before': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: '60%',
                          borderRadius: '0 4px 4px 0',
                          backgroundColor: 'var(--accent)',
                        }
                      : {},
                    '&:hover': {
                      backgroundColor: active ? 'var(--accent-soft)' : 'var(--surface-sunk)',
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: showExpanded ? 44 : 0,
                      justifyContent: 'center',
                      color: active ? 'var(--accent-deep)' : 'var(--ink-muted)',
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <AnimatePresence>
                    {showExpanded && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        style={{ flex: 1 }}
                      >
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: active ? 700 : 600,
                            fontSize: '0.9rem',
                            fontFamily: '"Inter Tight", "Inter", sans-serif',
                            letterSpacing: '-0.015em',
                            color: active ? 'var(--ink)' : 'var(--ink-soft)',
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* User Section */}
      <Box
        sx={{
          p: showExpanded ? 2 : 1.5,
          borderTop: '1px solid var(--hairline)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: showExpanded ? 1.5 : 0,
            borderRadius: '14px',
            backgroundColor: showExpanded ? 'var(--surface-sunk)' : 'transparent',
            justifyContent: showExpanded ? 'flex-start' : 'center',
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              backgroundColor: 'var(--ink)',
              color: 'var(--bg)',
              fontSize: '0.85rem',
              fontWeight: 700,
              fontFamily: '"Inter Tight", "Inter", sans-serif',
            }}
          >
            {getUserInitials()}
          </Avatar>

          <AnimatePresence>
            {showExpanded && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                style={{ flex: 1, overflow: 'hidden' }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontFamily: '"Inter Tight", "Inter", sans-serif',
                    fontSize: '0.85rem',
                    letterSpacing: '-0.015em',
                    color: 'var(--ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isAdmin ? 'Admin' : 'Account'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: 'var(--ink-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.email || ''}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: showExpanded ? 'flex-start' : 'center',
            flexDirection: showExpanded ? 'row' : 'column',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Sign out" placement="top">
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                width: 38,
                height: 38,
                backgroundColor: 'var(--error-soft)',
                borderRadius: '10px',
                color: 'var(--error)',
                '&:hover': {
                  backgroundColor: 'var(--error-soft)',
                  filter: 'brightness(0.95)',
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!isMobile && (
        <Box sx={{ px: showExpanded ? 2 : 1.5, pb: 2 }}>
          <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} placement="right" arrow>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                width: '100%',
                height: 40,
                borderRadius: '10px',
                color: 'var(--ink-muted)',
                backgroundColor: 'var(--surface)',
                border: '1px solid var(--hairline)',
                '&:hover': {
                  backgroundColor: 'var(--surface-sunk)',
                  color: 'var(--accent)',
                },
              }}
            >
              <motion.div
                animate={{ rotate: isCollapsed ? 180 : 0 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <CollapseIcon sx={{ fontSize: 20 }} />
              </motion.div>
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={handleMobileToggle}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1300,
            width: 48,
            height: 48,
            borderRadius: '14px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--hairline)',
            color: 'var(--ink)',
            '&:hover': { backgroundColor: 'var(--surface-sunk)' },
          }}
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>

        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH_EXPANDED,
              border: 'none',
              backgroundColor: 'var(--bg-alt)',
            },
          }}
          ModalProps={{ keepMounted: true }}
        >
          {navigationContent}
        </Drawer>
      </>
    );
  }

  return (
    <MotionBox
      component="nav"
      initial={false}
      animate={{ width: currentWidth }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      sx={{
        height: '100vh',
        position: 'relative',
        flexShrink: 0,
        borderRight: '1px solid var(--hairline)',
      }}
    >
      {navigationContent}
    </MotionBox>
  );
};

export default NavigationBar;
